
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useUndoHistory } from '@/hooks/useUndoHistory';
import { showErrorToast, showSuccessToast } from '@/utils/toast-utils';
import { MealsState, MealType, FoodItem, DailyNutrients, MealSlot } from '@/types/meal-plan';
import { ClientIntakeData } from '@/components/meal-planning/ClientIntakeForm';
import { copyManualMealPlanDay } from '@/lib/apiService';
import { normalizeManualDraftApiResponse, isManualDraftApiShape } from '@/utils/meal-plan-draft-normalizer';

interface MealPlanStateOptions {
  mealTypes?: Array<{
    mealType: string;
    mealName: string;
    targetCalories: number;
    timeSlot?: string;
    uniqueId?: string;
  }>;
  draftId?: string;
}

// Stable empty array reference to prevent re-renders
const EMPTY_MEAL_TYPES: Array<{
  mealType: string;
  mealName: string;
  targetCalories?: number;
  timeSlot?: string;
  uniqueId?: string;
}> = [];

export const useMealPlanState = (options: MealPlanStateOptions = {}) => {
  const { mealTypes: customMealTypes, draftId } = options;
  const [planDuration, setPlanDuration] = useState(1);
  const [days, setDays] = useState([1]);
  const [activeDay, setActiveDay] = useState(1);
  const [nutrientGuideline, setNutrientGuideline] = useState<string>("US IOM 2010 (Institute of Medicine)");
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  
  // Use provided meal types (default to empty array if not provided)
  // Use a ref to cache previous normalized meal types for stability
  type MealTypesArray = Array<{
    mealType: string;
    mealName: string;
    targetCalories?: number;
    timeSlot?: string;
    uniqueId?: string;
  }>;
  const mealTypesCacheRef = useRef<{ str: string; value: MealTypesArray }>({ str: '', value: EMPTY_MEAL_TYPES });
  
  // Normalize empty arrays to stable reference to prevent unnecessary re-renders
  // Memoize based on actual content, not reference
  const normalizedMealTypes = useMemo(() => {
    const currentStr = customMealTypes ? JSON.stringify(customMealTypes) : '';
    
    // If content is the same, return cached reference
    if (mealTypesCacheRef.current.str === currentStr && mealTypesCacheRef.current.value) {
      return mealTypesCacheRef.current.value;
    }
    
    // Otherwise, create new normalized value
    const normalized = (!customMealTypes || customMealTypes.length === 0) 
      ? EMPTY_MEAL_TYPES 
      : customMealTypes;
    
    // Update cache
    mealTypesCacheRef.current = { str: currentStr, value: normalized };
    return normalized;
  }, [customMealTypes ? JSON.stringify(customMealTypes) : '']);
  
  // Use JSON.stringify to create a stable dependency that only changes when content changes
  const activeMealTypesStr = useMemo(() => JSON.stringify(normalizedMealTypes), [normalizedMealTypes]);
  // Stable reference - already normalized and memoized
  const activeMealTypes = normalizedMealTypes;

  // State for meal information (names, times, kcal targets)
  const [mealInfo, setMealInfo] = useState<{
    [day: number]: {
      [key: string]: {
        name: string;
        timeSlot: string;
        targetKcal?: number | null;
      };
    };
  }>({});

  // Generate initial meals structure based on meal types (memoized)
  const generateMealsStructure = useCallback(() => {
    const structure: any = {};
    activeMealTypes.forEach(meal => {
      // Use uniqueId if available, otherwise use mealType
      const key = meal.uniqueId || meal.mealType;
      structure[key] = [];
    });
    return structure;
  }, [activeMealTypesStr]); // Use stable string dependency

  const initialMeals: MealsState = useMemo(() => ({
    1: generateMealsStructure()
  }), [generateMealsStructure]);

  const {
    state: meals,
    setState: setMeals,
    saveStateForUndo,
    undo,
    canUndo,
    lastAction,
    hideUndo
  } = useUndoHistory<MealsState>(initialMeals);

  // Track if we've initialized to prevent infinite loops
  const initializedRef = useRef<{ mealInfo: string; meals: string }>({ mealInfo: '', meals: '' });

  // Initialize meal info for all days on mount and when meal types change
  useEffect(() => {
    const mealInfoKey = `${activeMealTypesStr}-${days.length}`;
    
    // Skip if already initialized for this exact combination
    if (initializedRef.current.mealInfo === mealInfoKey) {
      return;
    }

    const newMealInfo: typeof mealInfo = {};
    days.forEach(day => {
      const dayMealInfo: { [key: string]: { name: string; timeSlot: string; targetKcal?: number | null } } = {};
      activeMealTypes.forEach(meal => {
        // Use uniqueId if available, otherwise use mealType
        const key = meal.uniqueId || meal.mealType;
        dayMealInfo[key] = {
          name: meal.mealName,
          timeSlot: meal.timeSlot || '',
          targetKcal: meal.targetCalories
        };
      });
      newMealInfo[day] = dayMealInfo;
    });
    
    setMealInfo(newMealInfo);
    initializedRef.current.mealInfo = mealInfoKey;
  }, [activeMealTypesStr, days.length, activeMealTypes]); // Include activeMealTypes for completeness

  // Reinitialize meals when meal types or days change (only if structure actually changed)
  useEffect(() => {
    const mealsKey = `${activeMealTypesStr}-${days.length}`;
    
    // Skip if already initialized for this exact combination
    if (initializedRef.current.meals === mealsKey) {
      return;
    }

    const newMeals = days.reduce<MealsState>((acc, day) => {
      acc[day] = generateMealsStructure();
      return acc;
    }, {});
    
    setMeals(newMeals);
    initializedRef.current.meals = mealsKey;
  }, [activeMealTypesStr, days.length, generateMealsStructure, setMeals]); // Include all dependencies

  // Initialize meal info for a day using dynamic meal types
  const initializeMealInfo = useCallback((day: number) => {
    const dayMealInfo: { [key: string]: { name: string; timeSlot: string; targetKcal?: number | null } } = {};
    
    activeMealTypes.forEach(meal => {
      // Use uniqueId if available, otherwise use mealType
      const key = meal.uniqueId || meal.mealType;
      dayMealInfo[key] = {
        name: meal.mealName,
        timeSlot: meal.timeSlot || '',
        targetKcal: meal.targetCalories
      };
    });

    setMealInfo(prev => ({
      ...prev,
      [day]: dayMealInfo
    }));
  }, [activeMealTypes]);

  // Get meal slots with enhanced information using dynamic meal types
  const getMealSlots = useCallback((day: number): MealSlot[] => {
    const dayMeals = meals[day] || generateMealsStructure();
    const dayMealInfo = mealInfo[day];

    return activeMealTypes.map((mealTypeConfig, index) => {
      // Use uniqueId for internal state key, but keep clean mealType for API calls
      const stateKey = mealTypeConfig.uniqueId || mealTypeConfig.mealType;
      const mealType = mealTypeConfig.mealType;
      const items = dayMeals[stateKey] || [];
      const info = dayMealInfo?.[stateKey] || {
        name: mealTypeConfig.mealName,
        timeSlot: mealTypeConfig.timeSlot || '',
        targetKcal: mealTypeConfig.targetCalories
      };

      return {
        id: `${day}-${stateKey}-${index}`,
        type: mealType as MealType,
        mealType: mealType, // Clean mealType without index for API calls
        stateKey: stateKey, // Internal key for state management
        name: info.name || mealTypeConfig.mealName,
        timeSlot: info.timeSlot || mealTypeConfig.timeSlot || '',
        targetKcal: info.targetKcal || mealTypeConfig.targetCalories,
        targetCalories: info.targetKcal || mealTypeConfig.targetCalories,
        foodItems: items,
        items
      };
    });
  }, [meals, mealInfo, activeMealTypes]);

  // Update meal information
  const updateMealInfo = (day: number, mealType: MealType, updates: { name?: string; timeSlot?: string; targetKcal?: number | null }, stateKey?: string) => {
    // Use stateKey if provided, otherwise fall back to mealType
    const key = stateKey || mealType;
    setMealInfo(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        [key]: {
          ...prev[day]?.[key],
          ...updates
        }
      }
    }));
  };
  
  const handleDurationChange = (duration: number) => {
    setPlanDuration(duration);
    const newDays = Array.from({ length: duration }, (_, i) => i + 1);
    setDays(newDays);
    
    const newMeals = newDays.reduce<MealsState>((acc, day) => {
      acc[day] = generateMealsStructure();
      return acc;
    }, {});
    
    // Meal info will be initialized automatically by the useEffect
    
    saveStateForUndo(newMeals, 'change duration');
    setActiveDay(1);
  };

  const calculateDailyNutrients = (day: number): DailyNutrients => {
    const dayMeals = meals[day];
    if (!dayMeals) {
      return {
        totalCalories: 0,
        totalProtein: 0,
        totalCarbs: 0,
        totalFat: 0,
        totalFiber: 0,
        calories: 0,
        protein: 0,
        carbs: 0,
        fat: 0,
        micronutrients: {
          vitaminA: 0,
          vitaminC: 0,
          vitaminD: 0,
          calcium: 0,
          iron: 0,
          sodium: 0,
          potassium: 0,
        }
      };
    }

    const toFiniteNumber = (value: unknown): number => {
      const parsed = typeof value === 'string' ? Number(value) : value;
      return typeof parsed === 'number' && Number.isFinite(parsed) ? parsed : 0;
    };

    // Collect all food items from all meal slots (supports dynamic keys in manual drafts).
    const allFoodItems = (Object.values(dayMeals) as unknown[]).flatMap((mealItems) =>
      Array.isArray(mealItems) ? (mealItems as FoodItem[]) : [],
    );

    return allFoodItems.reduce(
      (total, food) => {
        const fiber = toFiniteNumber((food as { fiber?: unknown }).fiber);
        return {
          totalCalories: total.totalCalories + toFiniteNumber(food.calories),
          totalProtein: total.totalProtein + toFiniteNumber(food.protein),
          totalCarbs: total.totalCarbs + toFiniteNumber(food.carbs),
          totalFat: total.totalFat + toFiniteNumber(food.fat),
          totalFiber: total.totalFiber + fiber,
          calories: total.calories + toFiniteNumber(food.calories),
          protein: total.protein + toFiniteNumber(food.protein),
          carbs: total.carbs + toFiniteNumber(food.carbs),
          fat: total.fat + toFiniteNumber(food.fat),
          micronutrients: {
            vitaminA: total.micronutrients.vitaminA + toFiniteNumber(food.micronutrients?.vitaminA),
            vitaminC: total.micronutrients.vitaminC + toFiniteNumber(food.micronutrients?.vitaminC),
            vitaminD: total.micronutrients.vitaminD + toFiniteNumber(food.micronutrients?.vitaminD),
            calcium: total.micronutrients.calcium + toFiniteNumber(food.micronutrients?.calcium),
            iron: total.micronutrients.iron + toFiniteNumber(food.micronutrients?.iron),
            sodium: total.micronutrients.sodium + toFiniteNumber(food.micronutrients?.sodium),
            potassium: total.micronutrients.potassium + toFiniteNumber(food.micronutrients?.potassium),
          }
        };
      },
      {
        totalCalories: 0,
        totalProtein: 0,
        totalCarbs: 0,
        totalFat: 0,
        totalFiber: 0,
        calories: 0,
        protein: 0,
        carbs: 0,
        fat: 0,
        micronutrients: {
          vitaminA: 0,
          vitaminC: 0,
          vitaminD: 0,
          calcium: 0,
          iron: 0,
          sodium: 0,
          potassium: 0,
        }
      }
    );
  };

  const handleAddFood = (food: FoodItem, day: number, mealType: MealType, stateKey?: string) => {
    const newMeals = { ...meals };
    if (!newMeals[day]) {
      newMeals[day] = generateMealsStructure();
    }
    // Use stateKey if provided, otherwise fall back to mealType
    const key = stateKey || mealType;
    newMeals[day] = {
      ...newMeals[day],
      [key]: [...(newMeals[day][key] || []), food]
    };
    
    saveStateForUndo(newMeals, 'add food');
  };

  const handleRemoveFood = (foodIndex: number, day: number, mealType: MealType, stateKey?: string) => {
    const newMeals = { ...meals };
    // Use stateKey if provided, otherwise fall back to mealType
    const key = stateKey || mealType;
    if (newMeals[day] && newMeals[day][key]) {
      newMeals[day] = {
        ...newMeals[day],
        [key]: newMeals[day][key].filter((_, index) => index !== foodIndex)
      };
      saveStateForUndo(newMeals, 'remove food');
    }
  };

  const handleSwapFood = (oldFood: FoodItem, newFood: FoodItem, day: number, mealType: MealType, stateKey?: string) => {
    const newMeals = { ...meals };
    // Use stateKey if provided, otherwise fall back to mealType
    const key = stateKey || mealType;
    if (newMeals[day] && newMeals[day][key]) {
      const foodIndex = newMeals[day][key].findIndex(food => food.id === oldFood.id);
      if (foodIndex !== -1) {
        newMeals[day] = {
          ...newMeals[day],
          [key]: newMeals[day][key].map((food, index) => 
            index === foodIndex ? newFood : food
          )
        };
        saveStateForUndo(newMeals, 'swap food');
      }
    }
  };

  const handleCopyDay = async (fromDay: number, toDay: number): Promise<any | void> => {
    if (!draftId) {
      showErrorToast("Cannot copy without a draft. Please create or load a draft.", "Draft not found");
      return;
    }

    try {
      const response = await copyManualMealPlanDay({
        draftId,
        sourceDay: fromDay,
        targetDay: toDay,
        targetDays: [toDay]
      });

      if (response.success) {
        if (response.data) {
          // Update local state strictly from API response
          loadFromDraft(response.data);
        }
        showSuccessToast("Day copied", `Day ${fromDay} has been copied to Day ${toDay}.`);
        return response.data;
      }
      throw new Error(response.message || 'Failed to copy day');
    } catch (error) {
      console.error('Error copying day via API:', error);
      showErrorToast(error, "Error");
    }
  };

  const handleCopyDays = async (fromDay: number, toDays: number[]): Promise<any | void> => {
    if (!toDays || toDays.length === 0) return;
    if (!draftId) {
      showErrorToast("Cannot copy without a draft. Please create or load a draft.", "Draft not found");
      return;
    }

    try {
      const response = await copyManualMealPlanDay({
        draftId,
        sourceDay: fromDay,
        targetDay: toDays[0],
        targetDays: toDays
      });

      if (response.success) {
        if (response.data) {
          // Update local state strictly from API response
          loadFromDraft(response.data);
        }
        showSuccessToast("Day copied", `Day ${fromDay} has been copied to days ${toDays.join(', ')}.`);
        return response.data;
      }
      throw new Error(response.message || 'Failed to copy day');
    } catch (error) {
      console.error('Error copying days via API:', error);
      showErrorToast(error, "Error");
    }
  };


  // Load meal plan from draft data
  const loadFromDraft = useCallback((draftData: any) => {
    const normalized = isManualDraftApiShape(draftData) ? normalizeManualDraftApiResponse(draftData) : draftData;
    if (!normalized?.suggestions || !Array.isArray(normalized.suggestions)) {
      return;
    }

    const loadedMeals: MealsState = {};
    const loadedMealInfo: typeof mealInfo = {};

    normalized.suggestions.forEach((daySuggestion: any) => {
      const day = daySuggestion.day;
      const dayMeals: any = {};
      const dayMealInfo: { [key: string]: { name: string; timeSlot: string; targetKcal?: number | null } } = {};

      // Initialize structure for this day
      activeMealTypes.forEach(mealTypeConfig => {
        const key = mealTypeConfig.uniqueId || mealTypeConfig.mealType;
        dayMeals[key] = [];
        dayMealInfo[key] = {
          name: mealTypeConfig.mealName,
          timeSlot: mealTypeConfig.timeSlot || '',
          targetKcal: mealTypeConfig.targetCalories
        };
      });

      // Load existing recipes into the structure and update meal info
      if (daySuggestion.meals) {
        Object.entries(daySuggestion.meals).forEach(([mealType, mealData]: [string, any]) => {
          // Find the matching meal type config by mealType or mealName
          const matchingConfig = activeMealTypes.find(mt => 
            mt.mealType === mealType || 
            mt.mealName === mealType ||
            mt.mealName === mealData.mealName
          );
          const key = matchingConfig?.uniqueId || mealType;

          const nameFromApi = mealData.mealName != null ? String(mealData.mealName).trim() : '';
          const timeFromApi =
            mealData.mealTime != null
              ? String(mealData.mealTime).trim()
              : mealData.timeSlot != null
                ? String(mealData.timeSlot).trim()
                : '';
          const kcalFromApi =
            typeof mealData.targetCalories === 'number' && Number.isFinite(mealData.targetCalories)
              ? mealData.targetCalories
              : undefined;

          dayMealInfo[key] = {
            name: nameFromApi || dayMealInfo[key]?.name || '',
            timeSlot: timeFromApi || dayMealInfo[key]?.timeSlot || '',
            targetKcal:
              kcalFromApi !== undefined ? kcalFromApi : (dayMealInfo[key]?.targetKcal ?? null),
          };

          const numFromNut = (item: any, key: string, nutKey?: string): number => {
            const nut = item?.nutrition ?? {};
            const v = item[key] ?? nut[key] ?? nut[nutKey ?? key];
            const n = typeof v === 'number' && !Number.isNaN(v) ? v : 0;
            return Math.round(n * 100) / 100;
          };

          const pickStableItemId = (...values: unknown[]): string => {
            for (const value of values) {
              const normalized = String(value ?? '').trim();
              if (normalized.length > 0) {
                return normalized;
              }
            }
            return '';
          };

          const getRecord = (value: unknown): Record<string, unknown> => (
            value && typeof value === 'object' ? value as Record<string, unknown> : {}
          );

          const pickMicronutrient = (...values: unknown[]): number => {
            for (const value of values) {
              const numeric = typeof value === 'string' ? Number(value) : value;
              if (typeof numeric === 'number' && Number.isFinite(numeric)) {
                return Math.round(numeric * 100) / 100;
              }
            }
            return 0;
          };

          const extractMicronutrients = (nutrition: unknown): NonNullable<FoodItem['micronutrients']> => {
            const nut = getRecord(nutrition);
            const micros = getRecord(nut.micros);
            const vitamins = getRecord(micros.vitamins);
            const minerals = getRecord(micros.minerals);

            return {
              vitaminA: pickMicronutrient(
                vitamins.vitaminA,
                vitamins.vitamin_a,
                micros.vitaminA,
                micros.vitamin_a,
                nut.vitaminA,
                nut.vitamin_a,
              ),
              vitaminC: pickMicronutrient(
                vitamins.vitaminC,
                vitamins.vitamin_c,
                micros.vitaminC,
                micros.vitamin_c,
                nut.vitaminC,
                nut.vitamin_c,
              ),
              vitaminD: pickMicronutrient(
                vitamins.vitaminD,
                vitamins.vitamin_d,
                micros.vitaminD,
                micros.vitamin_d,
                nut.vitaminD,
                nut.vitamin_d,
              ),
              calcium: pickMicronutrient(
                minerals.calcium,
                micros.calcium,
                nut.calcium,
              ),
              iron: pickMicronutrient(
                minerals.iron,
                micros.iron,
                nut.iron,
              ),
              sodium: pickMicronutrient(
                minerals.sodium,
                micros.sodium,
                nut.sodium,
              ),
              potassium: pickMicronutrient(
                minerals.potassium,
                micros.potassium,
                nut.potassium,
              ),
            };
          };

          const resolveReadyInMinutes = (recipe: any): number | undefined => {
            const directCandidates = [recipe?.readyInMinutes, recipe?.totalTimeMinutes, recipe?.totalMinutes];
            for (const candidate of directCandidates) {
              const numeric = Number(candidate);
              if (Number.isFinite(numeric) && numeric > 0) {
                return Math.round(numeric);
              }
            }

            const prep = Number(recipe?.prepTimeMinutes ?? recipe?.prepMinutes);
            const cook = Number(recipe?.cookTimeMinutes ?? recipe?.cookMinutes);
            const total = (Number.isFinite(prep) && prep > 0 ? prep : 0) + (Number.isFinite(cook) && cook > 0 ? cook : 0);
            return total > 0 ? Math.round(total) : undefined;
          };

          const recipeItems: FoodItem[] = Array.isArray(mealData.recipes)
            ? mealData.recipes.map((recipe: any) => {
                const nut = recipe.nutrition ?? {};
                const calories = numFromNut(recipe, 'calories') || (typeof nut.total_calories === 'number' ? Math.round(nut.total_calories * 100) / 100 : 0);
                const protein = numFromNut(recipe, 'protein');
                const carbs = numFromNut(recipe, 'carbs') || numFromNut(recipe, 'carbohydrate');
                const fat = numFromNut(recipe, 'fat') || numFromNut(recipe, 'total_fat');
                const recipeItemId = pickStableItemId(recipe.itemId, recipe._id, recipe.id);
                const recipeSourceId = pickStableItemId(recipe.recipeId, recipe.sourceId, recipe.id);
                const provider =
                  recipe.provider === 'internal' || recipe.provider === 'spoonacular' || recipe.provider === 'edamam'
                    ? recipe.provider
                    : recipe.provider === 'custom'
                      ? 'internal'
                    : recipe.source === 'cached'
                      ? 'internal'
                      : recipe.source === 'custom'
                        ? 'internal'
                      : recipe.source === 'edamam'
                        ? 'edamam'
                        : 'spoonacular';
                return {
                  id: recipeItemId || recipeSourceId,
                  sourceId: recipeSourceId || undefined,
                  name: recipe.title || recipe.recipeName || 'Unknown Recipe',
                  readyInMinutes: resolveReadyInMinutes(recipe),
                  calories,
                  protein,
                  carbs,
                  fat,
                  micronutrients: extractMicronutrients(nut),
                  category: 'recipe',
                  serving: '',
                  source: provider === 'edamam' ? 'edamam' : 'spoonacular',
                  provider,
                  healthLabels: recipe.healthLabels || [],
                  allergens: Array.isArray(recipe.allergens) ? recipe.allergens : [],
                  nutritionServings: recipe.nutritionServings,
                  servings: recipe.servings || 1,
                  defaultServingId: recipe.defaultServingId ?? null,
                  defaultServingGrams: recipe.defaultServingGrams ?? null,
                  selectedServingId: recipe.selectedServingId ?? recipe.defaultServingId ?? null,
                  selectedServingGrams: recipe.selectedServingGrams ?? recipe.defaultServingGrams ?? null,
                  servingSizeMultiplier: recipe.servingSizeMultiplier ?? 1,
                  servingSizes: Array.isArray(recipe.servingSizes) ? recipe.servingSizes : [],
                };
              })
            : [];

          const ingredientItems: FoodItem[] = Array.isArray(mealData.ingredients)
            ? mealData.ingredients.map((ing: any) => {
                const nut = ing.nutrition ?? {};
                const calories = numFromNut(ing, 'calories') || (typeof nut.total_calories === 'number' ? Math.round(nut.total_calories * 100) / 100 : 0);
                const protein = numFromNut(ing, 'protein');
                const carbs = numFromNut(ing, 'carbs') || numFromNut(ing, 'carbohydrate');
                const fat = numFromNut(ing, 'fat') || numFromNut(ing, 'total_fat');
                const amount = typeof ing.amount === 'number' ? ing.amount : 1;
                const servingId = ing.servingId ?? ing.unit ?? 'g';
                const serving = [amount, servingId].filter(Boolean).join(' ') || '';
                const ingredientItemId = pickStableItemId(
                  ing.itemId,
                  ing._id,
                  ing.id,
                  ing.recipeIngredientId,
                  ing.manualIngredientId,
                );
                const ingredientSourceId = pickStableItemId(
                  ing.sourceId,
                  ing.ingredientId,
                  ing.sourceIngredientId,
                  ing.id,
                );
                const provider =
                  ing.provider === 'internal' || ing.provider === 'spoonacular'
                    ? ing.provider
                    : ing.source === 'cached'
                      ? 'internal'
                      : 'spoonacular';
                const source = provider === 'internal' ? 'internal' : 'spoonacular';
                return {
                  id: ingredientItemId || ingredientSourceId,
                  sourceId: ingredientSourceId || undefined,
                  name: ing.name || 'Unknown Ingredient',
                  calories,
                  protein,
                  carbs,
                  fat,
                  micronutrients: extractMicronutrients(nut),
                  category: 'ingredient',
                  serving,
                  source,
                  provider,
                  healthLabels: [],
                  nutritionServings: amount,
                  servings: 1,
                  amount,
                  servingId,
                  amountGrams: typeof ing.amountGrams === 'number' && Number.isFinite(ing.amountGrams) ? ing.amountGrams : undefined,
                };
              })
            : [];

          dayMeals[key] = [...recipeItems, ...ingredientItems];
        });
      }

      loadedMeals[day] = dayMeals;
      loadedMealInfo[day] = dayMealInfo;
    });
    setMeals(loadedMeals);
    setMealInfo(loadedMealInfo);
  }, [activeMealTypes]);

  return {
    planDuration,
    days,
    activeDay,
    nutrientGuideline,
    isReportModalOpen,
    meals,
    mealInfo,
    getMealSlots,
    updateMealInfo,
    handleDurationChange,
    calculateDailyNutrients,
    handleAddFood,
    handleRemoveFood,
    handleSwapFood,
    handleCopyDay,
    handleCopyDays,
    loadFromDraft,
    setActiveDay,
    setNutrientGuideline,
    setIsReportModalOpen,
    undo,
    canUndo,
    lastAction,
    hideUndo
  };
};
